import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import {
  Lightbulb,
  CheckCircle,
  RadioButtonUnchecked
} from '@mui/icons-material';
import { brand } from '../../theme/colors';
import OfflineGuideDialog from './OfflineGuideDialog';

const GettingStartedChecklist = ({
  profileComplete,
  hasPortfolioItem,
  hasBrowsedServer,
  hasAcceptedVerification,
  hasCompletedVerification,
  onDismiss
}) => {
  const navigate = useNavigate();
  const [showOfflineGuide, setShowOfflineGuide] = useState(false);

  // Check both localStorage (instant, same device) and server flag (survives device switches)
  const hasBrowsed = localStorage.getItem('eqaya_verifier_browsed_gigs') === 'true' || !!hasBrowsedServer;
  const hasSeenOfflineGuide = localStorage.getItem('eqaya_offline_guide_seen') === 'true';

  const items = [
    {
      label: 'Complete your profile',
      link: '/profile',
      checked: !!profileComplete
    },
    {
      label: 'Browse available verifications',
      link: '/verifier/available-verifications',
      checked: hasBrowsed
    },
    {
      label: 'Accept your first verification',
      link: '/verifier/available-verifications',
      checked: !!hasAcceptedVerification
    },
    {
      label: 'Learn about offline mode',
      action: () => setShowOfflineGuide(true),
      checked: hasSeenOfflineGuide
    },
    {
      label: 'Complete a verification',
      link: '/verifier/my-work',
      checked: !!hasCompletedVerification
    }
  ];

  const completedCount = items.filter(i => i.checked).length;
  const progress = (completedCount / items.length) * 100;

  // Auto-dismiss when all complete
  if (completedCount === items.length) return null;

  return (
    <Card
      sx={{
        backgroundColor: 'white',
        borderLeft: `4px solid ${brand.accent || '#43B581'}`,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        mb: 3
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lightbulb sx={{ color: brand.accent || '#43B581' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              Getting Started
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#7f8c8d', fontWeight: 600 }}>
            {completedCount}/{items.length} complete
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 2,
            height: 6,
            borderRadius: 3,
            bgcolor: '#e8f5e9',
            '& .MuiLinearProgress-bar': {
              bgcolor: brand.accent || '#43B581',
              borderRadius: 3,
            }
          }}
        />

        <List disablePadding>
          {items.map((item, index) => (
            <ListItem
              key={index}
              button
              onClick={() => item.action ? item.action() : navigate(item.link)}
              sx={{
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.checked ? (
                  <CheckCircle sx={{ color: brand.accent || '#43B581' }} />
                ) : (
                  <RadioButtonUnchecked sx={{ color: '#9e9e9e' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: {
                    textDecoration: item.checked ? 'line-through' : 'none',
                    color: item.checked ? '#9e9e9e' : '#2c3e50'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            onClick={onDismiss}
            sx={{ color: '#7f8c8d', textTransform: 'none' }}
          >
            Dismiss
          </Button>
        </Box>
      </CardContent>

      {/* Offline Guide Dialog */}
      <OfflineGuideDialog
        open={showOfflineGuide}
        onClose={() => setShowOfflineGuide(false)}
      />
    </Card>
  );
};

export default GettingStartedChecklist;
